import { Dtm, getDtm } from "../enum/dtm";
import { SelfAttendanceData } from "./self-attendance-data";

export class HeroData {
    public title: string;
    public type: string;
    public contents: String[];

    constructor(heroData: IHeroData) {
        this.title = heroData.title;
        this.type = heroData.type;
        this.contents = heroData.contents;
    }
}

export class SelfAttendanceHeroData extends HeroData implements ISelfAttendanceHeroData {
    public dtmPageLoadedData: IDtmPageLoadedData[];
    public dtmCustomLoadedData: IDtmCustomLoadedData[];
    public hideInvoiceBankBilletMessage: boolean;
    public showInvoice: boolean;
    public buttonName: string;

    constructor({
        title,
        contents,
        type,
        hideInvoiceBankBilletMessage,
        dtmPageLoadedData = [],
        dtmCustomLoadedData = [],
        buttonName = "Voltar",
        showInvoice = true
    }: ISelfAttendanceHeroData) {
        super({ title, contents, type });
        this.dtmPageLoadedData = dtmPageLoadedData;
        this.hideInvoiceBankBilletMessage = hideInvoiceBankBilletMessage;
        this.showInvoice = showInvoice;
        this.dtmCustomLoadedData = dtmCustomLoadedData;
        this.buttonName = buttonName;
    }

    public static fromPreset(presetName: string | PresetName, params: ISelfAttendanceHeroDataParams): SelfAttendanceHeroData {
        if (!presets[presetName]) throw `Preset for ${presetName} not found.`

        return new presets[presetName](params);
    }
}

class Duplicate extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier,  selfAttendanceData}) {
        let title = "Geramos um boleto para você!";
        let contents = ['Caso já tenha pago, não se preocupe, o prazo de compensação do boleto é de até 3 dias úteis.'];

        if(classificationIdentifier === 'saude-do-seu-negocio') contents.unshift('Efetue o pagamento e não perca o acesso ao Saúde do seu Negócio.');

        if(selfAttendanceData.pixData){
            contents = [];
            title = "Geramos um PIX para você!"
        }

        super({
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.SegundaViaSucesso, true, getDtm(Dtm.SegundaViaSucesso, classificationIdentifier))],
            type: "new-success",
            title: title,
            contents: contents,
            hideInvoiceBankBilletMessage: true,
        });
    }
}

class InvoicingError extends SelfAttendanceHeroData {
    constructor({ selfAttendanceData, classificationIdentifier }) {
        let title;
        let contents;

        if (selfAttendanceData.creditCardData) {
            title = 'Seu pagamento foi efetuado com sucesso!';
            contents = [
                'Você receberá um email com o número do pedido do pagamento efetuado.',
            ];
        } else if (selfAttendanceData.bankBilletData) {
            title = 'Boleto gerado com sucesso!';
            contents = [
                'Assim que for efetuado o pagamento, o prazo de compensação do boleto é de até 3 dias úteis e você receberá um email confirmando sua compra.',
            ];
        }

        super({
            title: title,
            contents: contents,
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.RetencaoCartaoInvalidoSucesso, true, getDtm(Dtm.RetencaoCartaoInvalidoSucesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['Purchase', 'Transaction'],
                'Sydle-pagamento-invalido'
            )],
            type: 'new-success',
        });
    }
}

class ExpiredBankBillet extends SelfAttendanceHeroData {
    constructor({ selfAttendanceData, classificationIdentifier }) {
        let title;
        let contents;

        if (selfAttendanceData.creditCardData) {
            if (selfAttendanceData.creditCardData.chargedUser) {
                title = 'Pagamento efetuado com sucesso!';
                contents = [];
            } else {
                title = 'Forma de pagamento atualizada!';
                contents = [
                    'Alteramos a sua forma de pagamento para cartão de crédito.',
                ];
            }
        } else if (selfAttendanceData.bankBilletData) {
            title = 'Geramos um boleto para você!';
            if (classificationIdentifier === 'saude-do-seu-negocio') {
                contents = [
                    'Efetue o pagamento e não perca o acesso ao Saúde do seu Negócio.',
                ];
            } else if (classificationIdentifier === 'consulta-e-recuperacao-de-dividas') {
                contents = [
                    'Efetue o pagamento e não deixe de realizar consultas e recuperar dívidas.',
                ];
            }
        }

        super({
            title: title,
            type: "new-success",
            contents: contents,
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.RetencaoBoletoVencidoSucesso, true, getDtm(Dtm.RetencaoBoletoVencidoSucesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['Purchase', 'Transaction'],
                'Sydle-seu-boleto-venceu'
            )],
            hideInvoiceBankBilletMessage: true
        })
    }
}

class Cancellation extends SelfAttendanceHeroData {
    constructor({ selfAttendanceData, classificationIdentifier }) {
        let title;
        let contents;
        let type;
        let buttonName;

        if (selfAttendanceData.success === true) {
            type = 'new-success';
            title = 'Solicitação de cancelamento concluída!';
        } else if (selfAttendanceData.success === false) {
            type = 'error';
            title = 'Erro na solicitação de cancelamento!';
        }

        buttonName = 'Ir para área logada';

        contents = selfAttendanceData.result.split('\n');

        super({
            showInvoice: false,
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.CancelamentoSucceso, true, getDtm(Dtm.CancelamentoSucceso, classificationIdentifier))],
            title: title,
            contents: contents,
            type: type,
            buttonName: buttonName
        })
    }
}

class RetentionPolicy extends SelfAttendanceHeroData {
    constructor({ selfAttendanceData, classificationIdentifier }) {
        let title;
        let contents;
        let type;
        let buttonName;

        if (selfAttendanceData.success === true) {
            type = 'new-success';
            title = 'Renovação com desconto programada com sucesso!';
        } else if (selfAttendanceData.success === false) {
            type = 'error';
            title = 'Erro na renovação do plano!';
        }

        buttonName = 'Ir para área logada';

        contents = selfAttendanceData.result.split('\n');

        super({
            showInvoice: false,
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.RetencaoSucesso, true, getDtm(Dtm.RetencaoSucesso, classificationIdentifier))],
            title: title,
            contents: contents,
            type: type,
            buttonName: buttonName
        });
    }
}

class PaymentChange extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier, selfAttendanceData }) {
        let title;
        let contents;
        let buttonName = 'Ir para área logada';

        if (selfAttendanceData.creditCardData) {
            if (selfAttendanceData.creditCardData.chargedUser) {
                title = 'Pagamento efetuado com sucesso!';
                contents = [];
            } else {
                title = 'Forma de pagamento atualizada!';
                contents = [
                    'Alteramos a sua forma de pagamento para cartão de crédito.',
                ];
            }
        } else if (selfAttendanceData.bankBilletData) {
            if (selfAttendanceData.bankBilletData.chargedUser) {
                title = 'Geramos um boleto para você!';
                if (classificationIdentifier === 'saude-do-seu-negocio') {
                    contents = [
                        'Efetue o pagamento e não perca o acesso ao Saúde do seu Negócio.',
                    ];
                } else if (classificationIdentifier === 'consulta-e-recuperacao-de-dividas') {
                    contents = [
                        'Efetue o pagamento e não deixe de realizar consultas e recuperar dívidas.',
                    ];
                }
            } else {
                title = 'Forma de pagamento atualizada!';
                contents = [
                    'Alteramos a sua forma de pagamento para boleto.',
                ];
            }
        } else if (selfAttendanceData.pixData) {
            if (selfAttendanceData.pixData.chargedUser) {
                title = 'Geramos um Pix para você!';
                if (classificationIdentifier === 'saude-do-seu-negocio') {
                    contents = [
                        'Efetue o pagamento e não perca o acesso ao Saúde do seu Negócio.',
                    ];
                } else if (classificationIdentifier === 'consulta-e-recuperacao-de-dividas') {
                    contents = [
                        'Efetue o pagamento e não deixe de realizar consultas e recuperar dívidas.',
                    ];
                }
            } else {
                title = 'Forma de pagamento atualizada!';
                contents = [
                    'Alteramos a sua forma de pagamento para PIX.',
                ];
            }
        }
        super({
            title: title,
            contents: contents,
            type: "new-success",
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.AlteracaoDePagamentoSuccesso, true, getDtm(Dtm.AlteracaoDePagamentoSuccesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['Purchase', 'Transaction'],
                'Sydle-alterar-pagamento'
            )],
            buttonName: buttonName
        })
    }
}

class EarlyRenewal extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier, selfAttendanceData }) {
        let title;
        let contents;
        let buttonName = 'Ir para área logada';

        title = 'Renovação antecipada realizada com sucesso!';
        contents = [
            'Sua renovação foi processada com sucesso. Lembre-se que sua próxima cobrança será realizada na data original de renovação do seu plano.',
        ];


        super({
            title: title,
            contents: contents,
            type: "new-success",
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.AntecipacaoRenovacaoClienteSucesso, true, getDtm(Dtm.AntecipacaoRenovacaoClienteSucesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['Purchase', 'Transaction'],
                'Sydle-antecipacao-renovacao-cliente'
            )],
            buttonName: buttonName
        })
    }
}

class Upgrade extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier, selfAttendanceData }) {
        let title;
        let contents;
        let type;
        let buttonName;

        if (selfAttendanceData.bankBilletData) {
            type = 'new-success';
            title = 'Pedido gerado com sucesso!';
            contents = [];
        } else if (selfAttendanceData.pixData) {
            type = 'new-success';
            title = 'Pedido gerado com sucesso!';
            contents = [];
        } else if (selfAttendanceData.creditCardData) {
            type = 'new-success';
            title = 'Alteração de plano realizada com sucesso!';
            contents = [];
            buttonName = 'Ver meu relatório';
        } else if (selfAttendanceData.isInvestigate) {
            type = 'warn';
            title = 'Estamos quase lá!';
            contents = [];
        }
        super({
            title: title,
            type: type,
            contents: contents,
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.UpgradeSucesso, true, getDtm(Dtm.UpgradeSucesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['Purchase', 'Transaction'],
                'Sydle-upgrade'
            )],
            buttonName: buttonName
        })
    }
}

class ChangeAutoRecharge extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier, selfAttendanceData }) {
        const wallet = selfAttendanceData.account.wallet;
        const newPaymentCondition = selfAttendanceData.newPaymentCondition;
        const buttonName = 'Ir para área logada';

        let paymentCondition;
        if (newPaymentCondition) {
            const formatter = new Intl.NumberFormat("pt-BR", {style: "currency", currency: "BRL"});
            const value = formatter.format(newPaymentCondition.installmentValue);
            const parcela = newPaymentCondition.installments == 1 ? "parcela" : "parcelas";
            const conditionInFull = numeroPorExtenso(newPaymentCondition.installments);
            paymentCondition = `A condição de pagamento também foi alterada para ${conditionInFull} ${parcela} no valor de ${value}. Caso deseje alterá-la, basta acessar o menu de alteração de forma de pagamento na Central do Usuário.`;
        }

        let automaticRechargeValue = wallet.automaticRecharge.product.automaticRecharge;
        let automaticRechargeValueFormated = Number.isInteger(Number(automaticRechargeValue)) ? automaticRechargeValue : Number(automaticRechargeValue).toFixed(2).replace('.',',');

        let contents = [
            'O novo pacote de crédito selecionado estará vigente a partir da sua próxima recarga automática for inferior ou igual a R$' +
            automaticRechargeValueFormated + ".", paymentCondition
        ]

        super({
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.AlterarRecargaSucesso, true, getDtm(Dtm.AlterarRecargaSucesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['alterar-recarga'],
                'Sydle-alterar-recarga'
            )],

            type: 'new-success',
            title: 'Valor de pacote de crédito atualizado!',
            contents: contents,
            buttonName: buttonName
        });
    }

    public static numeroPorExtenso(numero) {
        const numerosExtenso = ["zero", "uma", "duas", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"];
        return numerosExtenso[numero];
    }
}

class ActivateAutoRecharge extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier, selfAttendanceData }) {
        let automaticRechargeValue = selfAttendanceData.account.wallet.automaticRecharge.product.automaticRecharge;
        let automaticRechargeValueFormated = Number.isInteger(Number(automaticRechargeValue)) ? automaticRechargeValue : Number(automaticRechargeValue).toFixed(2).replace('.',',');

        super({
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.AtivarRecargaSucesso, true, getDtm(Dtm.AtivarRecargaSucesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['ativar-recarga'],
                'Sydle-ativar-recarga'
            )],
            type: 'upgrade-info',
            title: 'Recarga garantida ativada!',
            contents: ['Quando seu crédito for igual ou menor que R$ ' + automaticRechargeValueFormated + ', será feita uma nova recarga no valor do pacote escolhido sem desconto aplicado.'],
            buttonName: 'Ir para área logada'
        })
    }
}

class AutoRechargePaymentCHange extends SelfAttendanceHeroData {
    constructor({ selfAttendanceData, classificationIdentifier }) {
        let automaticRechargeValue = selfAttendanceData.account.wallet.automaticRecharge.product.automaticRecharge;
        let automaticRechargeValueFormated = Number.isNaN(Number(automaticRechargeValue)) ? automaticRechargeValue : Number(automaticRechargeValue).toFixed(2).replace('.',',');

        super({
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.AlteracaoDePagamentoRecargaSucesso, true, getDtm(Dtm.AlteracaoDePagamentoSuccesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['alterar-pagamento-recarga'],
                'Sydle-alterar-pagamento-recarga'
            )],
            type: 'new-success',
            title: 'Forma de pagamento alterada!',
            contents: [
                'Quando seu crédito for igual ou menor que R$ ' + automaticRechargeValueFormated + ', será feita uma nova recarga no valor do pacote escolhido sem desconto aplicado.'
            ],
            buttonName: 'Ir para área logada'
        })
    }
}

class DeactivateAutoRecharge extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier, }) {
        super({
            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.DesativarRecargaSucesso, true, getDtm(Dtm.DesativarRecargaSucesso, classificationIdentifier)),
            getDtmPageLoadedData(Dtm.CancelamentoSucceso, true, getDtm(Dtm.CancelamentoSucceso, classificationIdentifier))],
            showInvoice: false,
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['desativar-recarga'],
                'Sydle-desativar-recarga'
            )],
            type: 'new-success',
            title: 'Recarga garantida desativada!',
            contents: [
                'Lembre-se que você poderá ativar a recarga garantida quando achar necessário. Esta opção oferece mais praticidade no dia a dia da sua empresa.'
            ],
            buttonName: 'Ir para área logada'
        })
    }
}

class PaymentChangeInvestigate extends SelfAttendanceHeroData {
    constructor({ classificationIdentifier }) {
        super({
            type: 'info',
            title: 'Solicitação recebida com sucesso!',
            contents: ['<p><b>Recebemos sua alteração de forma de pagamento e iniciamos a confirmação dos seus dados.</b></p>', 'Fique de olho em seu e-mail, em até 6 horas responderemos o seu pedido.'],

            dtmPageLoadedData: [getDtmPageLoadedData(Dtm.AlteracaoDePagamentoSuccesso, true, getDtm(Dtm.AlteracaoDePagamentoSuccesso, classificationIdentifier))],
            dtmCustomLoadedData: [getDtmCustomLoadedData(
                ['Purchase', 'Transaction'],
                'Sydle-alterar-pagamento'
            )],
        })
    }
}


export enum PresetName {
    Duplicate = "2via",
    InvoicingError = "invoicingError",
    ExpiredBankBillet = "expiredBankBillet",
    Cancellation = "cancellation",
    RetentionPolicy = "retention-policy",
    PaymentChange = "paymentChange",
    Upgrade = "upgrade",
    ChangeAutoRecharge = "CHANGE_AUTO_RECHARGE",
    ActivateAutoRecharge = "ACTIVATE_AUTO_RECHARGE",
    AutoRechargePaymentChange = "AUTO_RECHARGE_PAYMENT_CHANGE",
    DeactivateAutoRecharge = "DEACTIVATE_AUTO_RECHARGE",
    PaymentChangeInvestigate = "paymentChangeInvestigate",
    EarlyRenewal = "EARLY_RENEWAL"
}

const presets = {
    [PresetName.Duplicate]: Duplicate,
    [PresetName.InvoicingError]: InvoicingError,
    [PresetName.ExpiredBankBillet]: ExpiredBankBillet,
    [PresetName.Cancellation]: Cancellation,
    [PresetName.RetentionPolicy]: RetentionPolicy,
    [PresetName.PaymentChange]: PaymentChange,
    [PresetName.Upgrade]: Upgrade,
    [PresetName.ChangeAutoRecharge]: ChangeAutoRecharge,
    [PresetName.AutoRechargePaymentChange]: AutoRechargePaymentCHange,
    [PresetName.ActivateAutoRecharge]: ActivateAutoRecharge,
    [PresetName.DeactivateAutoRecharge]: DeactivateAutoRecharge,
    [PresetName.PaymentChangeInvestigate]: PaymentChangeInvestigate,
    [PresetName.EarlyRenewal]: EarlyRenewal,
}

interface IDtmPageLoadedData {
    pageName: string;
    logged?: boolean;
    mapped?: string;
}

interface IDtmCustomLoadedData {
    events: any[],
    checkout?: string,
    userInfo?: any,
    custom?: any,
}

interface IHeroData {
    title: string;
    type: string;
    contents: any[];
}

interface ISelfAttendanceHeroData extends IHeroData {
    dtmPageLoadedData?: IDtmPageLoadedData[];
    dtmCustomLoadedData?: IDtmCustomLoadedData[];
    hideInvoiceBankBilletMessage?: boolean;
    showInvoice?: boolean;
    buttonName?: string;
}

interface ISelfAttendanceHeroDataParams {
    classificationIdentifier?: string,
    selfAttendanceData?: SelfAttendanceData
}

function getDtmPageLoadedData(pageName, logged?, mapped?): IDtmPageLoadedData {
    return {
        pageName: pageName,
        logged: logged,
        mapped: mapped
    }
}

function numeroPorExtenso(numero) {
    const numerosExtenso = ["zero", "uma", "duas", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze"];
    return numerosExtenso[numero];
}

function getDtmCustomLoadedData(events, checkout?, userInfo?, custom?): IDtmCustomLoadedData {
    return {
        events: events,
        checkout: checkout,
        userInfo: userInfo,
        custom: custom
    }
}
